import { normalizeHexColor } from "./color-contrast";

const SAMPLE_SIZE = 48;
const MIN_ALPHA = 128;
const NEAR_WHITE = 240;
const NEAR_TRANSPARENT_BG = 250;
const MAX_CANDIDATES = 3;
const QUANTIZE = 24;

export interface LogoColorCandidate {
  hex: string;
  weight: number;
}

function rgbToHex(r: number, g: number, b: number): string {
  const toHex = (n: number) => n.toString(16).padStart(2, "0");
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`.toUpperCase();
}

function quantizeChannel(value: number): number {
  const bucket = Math.floor(value / (256 / QUANTIZE));
  return Math.min(QUANTIZE - 1, bucket) * Math.floor(256 / QUANTIZE);
}

function colorKey(r: number, g: number, b: number): string {
  return `${quantizeChannel(r)},${quantizeChannel(g)},${quantizeChannel(b)}`;
}

function isIgnorablePixel(r: number, g: number, b: number, a: number): boolean {
  if (a < MIN_ALPHA) return true;
  if (r >= NEAR_WHITE && g >= NEAR_WHITE && b >= NEAR_WHITE) return true;
  if (r >= NEAR_TRANSPARENT_BG && g >= NEAR_TRANSPARENT_BG && b >= NEAR_TRANSPARENT_BG) return true;
  return false;
}

/** Pure ranking helper — exported for unit tests. */
export function rankLogoColorBuckets(
  buckets: ReadonlyMap<string, { r: number; g: number; b: number; count: number }>,
  maxColors = MAX_CANDIDATES,
): LogoColorCandidate[] {
  const scored = [...buckets.values()].map((entry) => {
    const hex = rgbToHex(entry.r, entry.g, entry.b);
    const max = Math.max(entry.r, entry.g, entry.b);
    const min = Math.min(entry.r, entry.g, entry.b);
    const saturation = max === 0 ? 0 : (max - min) / max;
    return {
      hex,
      weight: entry.count,
      score: entry.count * (0.35 + saturation * 0.65),
    };
  });

  scored.sort((a, b) => b.score - a.score || b.weight - a.weight);

  const seen = new Set<string>();
  const out: LogoColorCandidate[] = [];
  for (const row of scored) {
    const normalized = normalizeHexColor(row.hex);
    if (!normalized || seen.has(normalized)) continue;
    seen.add(normalized);
    out.push({ hex: normalized, weight: row.weight });
    if (out.length >= maxColors) break;
  }
  return out;
}

function extractFromImageData(data: Uint8ClampedArray): LogoColorCandidate[] {
  const buckets = new Map<string, { r: number; g: number; b: number; count: number }>();

  for (let i = 0; i < data.length; i += 4) {
    const r = data[i]!;
    const g = data[i + 1]!;
    const b = data[i + 2]!;
    const a = data[i + 3]!;
    if (isIgnorablePixel(r, g, b, a)) continue;

    const key = colorKey(r, g, b);
    const existing = buckets.get(key);
    if (existing) {
      existing.count += 1;
    } else {
      buckets.set(key, {
        r: quantizeChannel(r) + Math.floor(256 / QUANTIZE) / 2,
        g: quantizeChannel(g) + Math.floor(256 / QUANTIZE) / 2,
        b: quantizeChannel(b) + Math.floor(256 / QUANTIZE) / 2,
        count: 1,
      });
    }
  }

  return rankLogoColorBuckets(buckets);
}

/**
 * Samples dominant accent-like colors from a logo image URL (canvas).
 * Returns an empty array when the image cannot be loaded or has no usable pixels.
 */
export function extractLogoColorsFromImage(
  image: CanvasImageSource,
  width: number,
  height: number,
): LogoColorCandidate[] {
  const canvas = document.createElement("canvas");
  canvas.width = SAMPLE_SIZE;
  canvas.height = SAMPLE_SIZE;
  const ctx = canvas.getContext("2d");
  if (!ctx) return [];

  ctx.drawImage(image, 0, 0, width, height, 0, 0, SAMPLE_SIZE, SAMPLE_SIZE);
  const imageData = ctx.getImageData(0, 0, SAMPLE_SIZE, SAMPLE_SIZE);
  return extractFromImageData(imageData.data);
}

export function loadLogoColorsFromUrl(url: string): Promise<LogoColorCandidate[]> {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      try {
        resolve(extractLogoColorsFromImage(img, img.naturalWidth, img.naturalHeight));
      } catch {
        resolve([]);
      }
    };
    img.onerror = () => resolve([]);
    img.src = url;
  });
}
